import { salesApi } from '../../../../config/apiClient';

/**
 * Yemek platformu ürün eşleştirme (Yemeksepeti, Uber Eats, Migros → bizim ürünler).
 * Eşleşen ürün, sipariş ödenince stoktan düşer. Sunucu: gts_sales_api FoodPlatformProductMapping.
 */

export type FoodPlatform = 'yemeksepeti' | 'trendyol' | 'migros';
export type MappingStatus = 'auto' | 'manual' | 'suggested' | 'unmatched' | 'ignored';

export interface FoodMappingSettings {
  matchThreshold: number;
  suggestThreshold: number;
  updatedByName?: string | null;
}

export interface FoodPlatformAccount {
  platform: FoodPlatform;
  accountKey: string;
  label: string;
  branchIds: string[];
  branchNames: string[];
  storeNames: string[];
  singleBranch: boolean;
}

export interface FoodCandidate {
  inventoryItemId: string;
  name: string;
  stockCode?: string | null;
  categoryId?: string | null;
  score: number;
}

export interface FoodMappingRef {
  id: number;
  branchId: string;
  branchName?: string | null;
  inventoryItemId?: string | null;
  inventoryItemName?: string | null;
  stockCode?: string | null;
  categoryId?: string | null;
  status: MappingStatus;
  score: number;
  updatedByName?: string | null;
  updatedAt?: string | null;
  notInBranchPriceList: boolean;
}

export interface FoodMappingRow {
  platform: FoodPlatform;
  accountKey: string;
  accountLabel: string;
  matchKey: string;
  externalProductId?: string | null;
  externalName: string;
  externalVariant?: string | null;
  soldQuantity: number;
  orderCount: number;
  branchCount: number;
  lastSeenAt?: string | null;
  account?: FoodMappingRef | null;
  branch?: FoodMappingRef | null;
  branchOverrides: FoodMappingRef[];
  suggestions: FoodCandidate[];
}

export interface FoodMappingList {
  settings: FoodMappingSettings;
  branchId?: string | null;
  branchName?: string | null;
  branchHasPriceList: boolean;
  accounts: FoodPlatformAccount[];
  rows: FoodMappingRow[];
}

export interface FoodMappingSaveItem {
  platform: FoodPlatform;
  accountKey: string;
  externalProductId?: string | null;
  externalName: string;
  externalVariant?: string | null;
  /** account: hesabın bütün şubeleri · branch: yalnız branchId */
  scope: 'account' | 'branch';
  branchId?: string | null;
  action: 'map' | 'ignore' | 'reset';
  inventoryItemId?: string | null;
}

export interface FoodResolveLine {
  index: number;
  externalProductId?: string | null;
  name: string;
  variant?: string | null;
}

export interface FoodResolvedLine {
  index: number;
  inventoryItemId?: string | null;
  inventoryItemName?: string | null;
  stockCode?: string | null;
  hasRecipe: boolean;
  hasMenu: boolean;
  categoryId?: string | null;
  status: MappingStatus;
  score: number;
}

const BASE = '/api/FoodPlatformProductMapping';

const num = (v: any): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
const pick = (o: any, name: string): any => (o ? o[name] ?? o[name.charAt(0).toUpperCase() + name.slice(1)] : undefined);

const toSettings = (s: any): FoodMappingSettings => ({
  matchThreshold: num(pick(s, 'matchThreshold')) || 85,
  suggestThreshold: num(pick(s, 'suggestThreshold')) || 65,
  updatedByName: pick(s, 'updatedByName') ?? null,
});

const toCandidate = (c: any): FoodCandidate => ({
  inventoryItemId: String(pick(c, 'inventoryItemId')),
  name: pick(c, 'name') || '',
  stockCode: pick(c, 'stockCode') ?? null,
  categoryId: pick(c, 'categoryId') ?? null,
  score: num(pick(c, 'score')),
});

const toRef = (r: any): FoodMappingRef | null =>
  r
    ? {
      id: num(pick(r, 'id')),
      branchId: pick(r, 'branchId') || '',
      branchName: pick(r, 'branchName') ?? null,
      inventoryItemId: pick(r, 'inventoryItemId') ?? null,
      inventoryItemName: pick(r, 'inventoryItemName') ?? null,
      stockCode: pick(r, 'stockCode') ?? null,
      categoryId: pick(r, 'categoryId') ?? null,
      status: (pick(r, 'status') || 'unmatched') as MappingStatus,
      score: num(pick(r, 'score')),
      updatedByName: pick(r, 'updatedByName') ?? null,
      updatedAt: pick(r, 'updatedAt') ?? null,
      notInBranchPriceList: !!pick(r, 'notInBranchPriceList'),
    }
    : null;

const unwrap = (res: any): any => {
  const body = res?.data;
  if (body && (body.Success === false || body.success === false)) {
    throw new Error(body.Message || body.message || 'İşlem başarısız');
  }
  return body?.Data ?? body?.data;
};

const errorMessage = (e: any, fallback: string): string =>
  e?.response?.data?.Message || e?.response?.data?.message || e?.message || fallback;

export const foodPlatformMappingService = {
  /** Modal listesi. branchId boşsa şirket görünümü (bütün şubeler). */
  async list(branchId?: string | null): Promise<FoodMappingList> {
    try {
      const d = unwrap(await salesApi.post(`${BASE}/list`, { BranchId: branchId || null }));
      return {
        settings: toSettings(pick(d, 'settings')),
        branchId: pick(d, 'branchId') ?? null,
        branchName: pick(d, 'branchName') ?? null,
        branchHasPriceList: !!pick(d, 'branchHasPriceList'),
        accounts: (pick(d, 'accounts') || []).map((a: any) => ({
          platform: pick(a, 'platform'),
          accountKey: pick(a, 'accountKey'),
          label: pick(a, 'label') || '',
          branchIds: pick(a, 'branchIds') || [],
          branchNames: pick(a, 'branchNames') || [],
          storeNames: pick(a, 'storeNames') || [],
          singleBranch: !!pick(a, 'singleBranch'),
        })),
        rows: (pick(d, 'rows') || []).map((r: any): FoodMappingRow => ({
          platform: pick(r, 'platform'),
          accountKey: pick(r, 'accountKey'),
          accountLabel: pick(r, 'accountLabel') || '',
          matchKey: pick(r, 'matchKey'),
          externalProductId: pick(r, 'externalProductId') ?? null,
          externalName: pick(r, 'externalName') || '',
          externalVariant: pick(r, 'externalVariant') ?? null,
          soldQuantity: num(pick(r, 'soldQuantity')),
          orderCount: num(pick(r, 'orderCount')),
          branchCount: num(pick(r, 'branchCount')),
          lastSeenAt: pick(r, 'lastSeenAt') ?? null,
          account: toRef(pick(r, 'account')),
          branch: toRef(pick(r, 'branch')),
          branchOverrides: (pick(r, 'branchOverrides') || []).map(toRef).filter(Boolean),
          suggestions: (pick(r, 'suggestions') || []).map(toCandidate),
        })),
      };
    } catch (e) {
      throw new Error(errorMessage(e, 'Eşleştirmeler yüklenemedi'));
    }
  },

  async candidates(p: { branchId?: string | null; platform?: string; accountKey?: string; search?: string }): Promise<FoodCandidate[]> {
    const d = unwrap(await salesApi.post(`${BASE}/candidates`, {
      BranchId: p.branchId || null,
      Platform: p.platform || null,
      AccountKey: p.accountKey || null,
      Search: p.search || null,
      Take: 30,
    }));
    return (d || []).map(toCandidate);
  },

  async saveBatch(items: FoodMappingSaveItem[]): Promise<number> {
    try {
      const d = unwrap(await salesApi.post(`${BASE}/save-batch`, {
        Items: items.map(i => ({
          Platform: i.platform,
          AccountKey: i.accountKey,
          ExternalProductId: i.externalProductId || null,
          ExternalName: i.externalName,
          ExternalVariant: i.externalVariant || null,
          Scope: i.scope,
          BranchId: i.branchId || null,
          Action: i.action,
          InventoryItemId: i.inventoryItemId || null,
        })),
      }));
      return num(pick(d, 'changed'));
    } catch (e) {
      throw new Error(errorMessage(e, 'Eşleştirmeler kaydedilemedi'));
    }
  },

  async saveSettings(matchThreshold: number, suggestThreshold: number): Promise<FoodMappingSettings> {
    try {
      return toSettings(unwrap(await salesApi.post(`${BASE}/settings/save`, { MatchThreshold: matchThreshold, SuggestThreshold: suggestThreshold })));
    } catch (e) {
      throw new Error(errorMessage(e, 'Eşik kaydedilemedi'));
    }
  },

  /** Ödeme anında: sipariş satırlarını bizim ürünlere çözer. */
  async resolve(p: { platform: FoodPlatform; branchId?: string | null; platformIntegrationId?: number | null; lines: FoodResolveLine[] }): Promise<FoodResolvedLine[]> {
    const d = unwrap(await salesApi.post(`${BASE}/resolve`, {
      Platform: p.platform,
      BranchId: p.branchId || null,
      PlatformIntegrationId: p.platformIntegrationId ?? null,
      Lines: p.lines.map(l => ({ Index: l.index, ExternalProductId: l.externalProductId || null, Name: l.name, Variant: l.variant || null })),
    }));
    return (d || []).map((r: any): FoodResolvedLine => ({
      index: num(pick(r, 'index')),
      inventoryItemId: pick(r, 'inventoryItemId') ?? null,
      inventoryItemName: pick(r, 'inventoryItemName') ?? null,
      stockCode: pick(r, 'stockCode') ?? null,
      hasRecipe: !!pick(r, 'hasRecipe'),
      hasMenu: !!pick(r, 'hasMenu'),
      categoryId: pick(r, 'categoryId') ?? null,
      status: (pick(r, 'status') || 'unmatched') as MappingStatus,
      score: num(pick(r, 'score')),
    }));
  },
};

export const PLATFORM_LABELS: Record<string, string> = {
  yemeksepeti: 'Yemeksepeti',
  trendyol: 'Uber Eats',
  migros: 'Migros',
};

export const PLATFORM_COLORS: Record<string, string> = {
  yemeksepeti: '#d6224a',
  trendyol: '#1f8a3b',
  migros: '#e07b00',
};
